import type { Polygon } from 'geojson';

import type { CityFile } from '../../shared/cityFile';
import type { Opportunity } from '../../shared/opportunity';

/**
 * How far you can get from a point in a given time, and how long a given
 * event takes to reach (contract §12).
 *
 * The two must agree exactly: an event's point is inside the walkshed polygon
 * if and only if `travelMinutes` is at or under the slider's value. So both
 * are read off one shape. The polygon is a 32-point superellipse (a squared-off
 * circle, which reads as a street grid rather than a compass rose), and the
 * distance to a point is that polygon's own gauge: how many times the unit
 * shape has to be scaled to reach it. Scaling the unit shape by the radius
 * draws the walkshed; measuring a point's gauge and dividing by the speed times
 * it. Inside one is under the other, by construction.
 *
 * Geometric, not routed: since Session 9 this is the fallback, used when
 * TravelTime is unavailable or fails (`travelTimes.ts`), and the dashed shape
 * drawn from a new origin until its isochrone lands.
 */

export type TravelMode = 'walk' | 'bike' | 'transit';
export const TRAVEL_MODES: TravelMode[] = ['walk', 'bike', 'transit'];

/** [lat, lng], as everywhere else in the app. */
export type LatLng = [number, number];

/** What the maths needs from the city file. A whole `CityFile` will do. */
export type TravelCity = Pick<CityFile, 'neighborhoods' | 'travel'>;

/** The slider's stops: five-minute steps to an hour, then fifteen to ninety (contract §11). */
export const DISTANCE_STEPS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 75, 90] as const;
/** Where the slider starts when a home is first set. */
export const DEFAULT_WITHIN = 30;
/** Transit, because it is what most New Yorkers mean by "near". */
export const DEFAULT_MODE: TravelMode = 'transit';

/**
 * Minutes lost before moving at all. Walking starts at the door; a bike has to
 * be unlocked or docked; transit has the walk to the station and the wait,
 * which the city file sets (`transitOverheadMin`). Walk and bike are fixed here
 * rather than added to the file's shape.
 */
export function overheadMinutes(mode: TravelMode, city: TravelCity): number {
  if (mode === 'transit') return city.travel.transitOverheadMin;
  return mode === 'bike' ? 3 : 0;
}

export function speedKmh(mode: TravelMode, city: TravelCity): number {
  const t = city.travel;
  return mode === 'walk' ? t.walkKmh : mode === 'bike' ? t.bikeKmh : t.transitKmh;
}

/** The walkshed's reach in km: speed × (minutes − overhead), never negative. */
export function radiusKm(mode: TravelMode, minutes: number, city: TravelCity): number {
  return (speedKmh(mode, city) * Math.max(0, minutes - overheadMinutes(mode, city))) / 60;
}

// --- the shape ------------------------------------------------------------------

const VERTICES = 32;
/** 2 is a circle, higher is squarer. 3 is squared-off without reading as a box. */
const EXPONENT = 3;

/** The unit superellipse, in km east and north of its centre, counter-clockwise from east. */
const UNIT: [number, number][] = Array.from({ length: VERTICES }, (_, i) => {
  const a = (2 * Math.PI * i) / VERTICES;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [Math.sign(c) * Math.abs(c) ** (2 / EXPONENT), Math.sign(s) * Math.abs(s) ** (2 / EXPONENT)];
});

/**
 * Each edge as an outward normal and its distance from the centre. A point is
 * inside the shape scaled by r exactly when, for every edge, n·p ≤ r·h.
 */
const EDGES = UNIT.map(([x1, y1], i) => {
  const [x2, y2] = UNIT[(i + 1) % VERTICES];
  // Counter-clockwise, so the outward normal is the edge turned clockwise.
  const nx = y2 - y1;
  const ny = x1 - x2;
  return { nx, ny, h: nx * x1 + ny * y1 };
});

/** The gauge: the smallest scale of the unit shape that reaches (x, y) km. */
function gauge(x: number, y: number): number {
  let g = 0;
  for (const { nx, ny, h } of EDGES) g = Math.max(g, (nx * x + ny * y) / h);
  return g;
}

// --- degrees and km, near the origin ----------------------------------------------

const KM_PER_DEG_LAT = 110.574;
const kmPerDegLng = (lat: number) => 111.32 * Math.cos((lat * Math.PI) / 180);

/**
 * Km east and north of the origin. Linear in degrees, scaled at the origin's
 * latitude, so a straight edge in km is a straight edge in degrees and the
 * polygon's own edges keep the gauge exact.
 */
function offsetKm(origin: LatLng, point: LatLng): [number, number] {
  return [(point[1] - origin[1]) * kmPerDegLng(origin[0]), (point[0] - origin[0]) * KM_PER_DEG_LAT];
}

// --- events ------------------------------------------------------------------------

const centroidCache = new WeakMap<TravelCity['neighborhoods'], Map<string, LatLng>>();

function centroids(city: TravelCity): Map<string, LatLng> {
  let map = centroidCache.get(city.neighborhoods);
  if (!map) {
    map = new Map(city.neighborhoods.map((n) => [n.key, [n.lat, n.lng] as LatLng]));
    centroidCache.set(city.neighborhoods, map);
  }
  return map;
}

export interface EventPoint {
  point: LatLng;
  /** Measured from the neighborhood's centroid: the UI says "≈" and draws the pin hollow. */
  approximate: boolean;
}

/**
 * Where an event is, for measuring to: its pin when it has a street address,
 * else its neighborhood's centroid, else nowhere. An online event is nowhere,
 * whatever neighborhood its organiser is in.
 */
export function eventPoint(event: Opportunity, city: TravelCity): EventPoint | null {
  if (event.format === 'virtual') return null;
  if (event.locationType === 'address' && event.latitude != null && event.longitude != null) {
    return { point: [event.latitude, event.longitude], approximate: false };
  }
  const centroid = event.neighborhoodKey ? centroids(city).get(event.neighborhoodKey) : undefined;
  return centroid ? { point: centroid, approximate: true } : null;
}

/**
 * Minutes from `origin` to a point, rounded up: rounding up is what keeps
 * "≤ the slider" and "inside the polygon" the same test for whole minutes.
 */
export function minutesTo(origin: LatLng, point: LatLng, mode: TravelMode, city: TravelCity): number {
  const [x, y] = offsetKm(origin, point);
  return Math.ceil(overheadMinutes(mode, city) + (gauge(x, y) / speedKmh(mode, city)) * 60);
}

/** Minutes from `origin` to the event, or null when the event has no point. */
export function travelMinutes(origin: LatLng, event: Opportunity, mode: TravelMode, city: TravelCity): number | null {
  const at = eventPoint(event, city);
  return at ? minutesTo(origin, at.point, mode, city) : null;
}

/**
 * Everywhere reachable from `origin` within `minutes`, as a closed GeoJSON ring
 * ([lng, lat] pairs). Collapses to the origin itself when the overhead alone
 * uses the time up.
 */
export function walkshed(origin: LatLng, mode: TravelMode, minutes: number, city: TravelCity): Polygon {
  const r = radiusKm(mode, minutes, city);
  const perLng = kmPerDegLng(origin[0]);
  const ring = UNIT.map(([x, y]) => [origin[1] + (x * r) / perLng, origin[0] + (y * r) / KM_PER_DEG_LAT]);
  ring.push(ring[0]);
  return { type: 'Polygon', coordinates: [ring] };
}
